import { useEffect, useState, useMemo, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { Sun, Moon, LogOut, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import Compatibility from "@/pages/Compatibility";
import ScrapeControl from "@/pages/ScrapeControl";
import Diff from "@/pages/Diff";
import VehicleDetail from "@/pages/VehicleDetail";
import EcuDetail from "@/pages/EcuDetail";
import Heatmap from "@/pages/Heatmap";
import InstantSearchPage from "@/pages/InstantSearchPage";
import AlientechCompatibility from "@/pages/AlientechCompatibility";
import AlientechScrapeControl from "@/pages/AlientechScrapeControl";
import MagicMotorsportCompatibility from "@/pages/MagicMotorsportCompatibility";
import MagicMotorsportScrapeControl from "@/pages/MagicMotorsportScrapeControl";
import DynoecuCompatibility from "@/pages/DynoecuCompatibility";
import DynoecuScrapeControl from "@/pages/DynoecuScrapeControl";
import BoschEcuReferences from "@/pages/BoschEcuReferences";
import Login from "@/pages/Login";
import fuxIcon from "@/assets/fuxicon.svg";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      window.dispatchEvent(new Event("auth:unauthorized"));
    }
    return Promise.reject(err);
  },
);

function useTheme() {
  const [theme, setTheme] = useState(
    () => document.documentElement.getAttribute("data-theme") || "dark",
  );

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem("theme", theme);
    } catch (e) {
      // ignore (e.g. storage disabled)
    }
  }, [theme]);

  const toggle = useCallback(
    () => setTheme((t) => (t === "dark" ? "light" : "dark")),
    [],
  );

  return [theme, toggle];
}

function ThemeToggle({ theme, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid="theme-toggle"
      title={theme === "dark" ? "Helles Theme" : "Dunkles Theme"}
      aria-label="Theme wechseln"
      className="ml-1 p-2 rounded-md text-neutral-300 hover:text-neutral-50 hover:bg-neutral-800/60 transition"
    >
      {theme === "dark" ? (
        <Sun className="w-4 h-4" />
      ) : (
        <Moon className="w-4 h-4" />
      )}
    </button>
  );
}

function Shell({ children, theme, onToggleTheme, onLogout }) {
  return (
    <div className="min-h-screen bg-page text-neutral-100">
      <header className="border-b border-neutral-800 bg-page/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="brand-link"
            className="flex items-center gap-1.5 group"
          >
            <span
              className="h-7 w-5 shrink-0 bg-[#D32222]"
              style={{
                WebkitMask: `url(${fuxIcon}) center / contain no-repeat`,
                mask: `url(${fuxIcon}) center / contain no-repeat`,
              }}
              aria-hidden="true"
            />
            <div>
              <div
                className="font-bold text-[21px] leading-none tracking-tight text-neutral-100"
                style={{ fontFamily: "'Orbitron', sans-serif" }}
              >
                tool<span className="text-[#D32222]">FUX</span>
              </div>
            </div>
          </Link>
          <nav className="flex items-center gap-1">
            <Link
              to="/suche"
              data-testid="nav-instant-search"
              className="px-3 py-1.5 rounded-md text-sm text-neutral-300 hover:text-neutral-50 hover:bg-neutral-800/60 transition"
            >
              Suche
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="nav-compat-dropdown"
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm text-neutral-300 hover:text-neutral-50 hover:bg-neutral-800/60 transition"
                >
                  Kompatibilität
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-panel border-neutral-800 text-neutral-100">
                <DropdownMenuItem asChild className="focus:bg-neutral-800">
                  <Link to="/" data-testid="nav-compat-autotuner">
                    Autotuner
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="focus:bg-neutral-800">
                  <Link to="/diff" data-testid="nav-diff">
                    Autotuner Diff
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="focus:bg-neutral-800">
                  <Link to="/alientech" data-testid="nav-compat-alientech">
                    Alientech
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="focus:bg-neutral-800">
                  <Link to="/magicmotorsport" data-testid="nav-compat-magicmotorsport">
                    MagicMotorsport
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="focus:bg-neutral-800">
                  <Link to="/original-ecu-dateien" data-testid="nav-compat-dynoecu">
                    Original ECU Dateien
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="nav-scraper-dropdown"
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm text-neutral-300 hover:text-neutral-50 hover:bg-neutral-800/60 transition"
                >
                  Scraper
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-panel border-neutral-800 text-neutral-100">
                <DropdownMenuItem asChild className="focus:bg-neutral-800">
                  <Link to="/scrape" data-testid="nav-scrape-autotuner">
                    Autotuner
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="focus:bg-neutral-800">
                  <Link to="/alientech/scrape" data-testid="nav-scrape-alientech">
                    Alientech Scraper
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="focus:bg-neutral-800">
                  <Link to="/magicmotorsport/scrape" data-testid="nav-scrape-magicmotorsport">
                    MagicMotorsport Scraper
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="focus:bg-neutral-800">
                  <Link to="/original-ecu-dateien/scrape" data-testid="nav-scrape-dynoecu">
                    Original ECU Dateien Scraper
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Link
              to="/heatmap"
              data-testid="nav-heatmap"
              className="px-3 py-1.5 rounded-md text-sm text-neutral-300 hover:text-neutral-50 hover:bg-neutral-800/60 transition"
            >
              Heatmap
            </Link>
            <Link
              to="/wissen/bosch-ecu-referenzen"
              data-testid="nav-bosch-ecu-references"
              className="px-3 py-1.5 rounded-md text-sm text-neutral-300 hover:text-neutral-50 hover:bg-neutral-800/60 transition"
            >
              Bosch-Codes
            </Link>
            <ThemeToggle theme={theme} onToggle={onToggleTheme} />
            <button
              type="button"
              onClick={onLogout}
              data-testid="logout-button"
              title="Abmelden"
              aria-label="Abmelden"
              className="ml-1 p-2 rounded-md text-neutral-300 hover:text-neutral-50 hover:bg-neutral-800/60 transition"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </nav>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-6 py-8">{children}</main>
    </div>
  );
}

function AppRoutes({ theme, toggleTheme, authState, setAuthState, handleLogout }) {
  const navigate = useNavigate();

  const handleLoginSuccess = useCallback(() => {
    setAuthState("authenticated");
    navigate("/suche");
  }, [navigate, setAuthState]);

  if (authState === "checking") {
    return <div className="min-h-screen bg-page" />;
  }

  if (authState !== "authenticated") {
    return <Login onSuccess={handleLoginSuccess} />;
  }

  return (
    <Shell theme={theme} onToggleTheme={toggleTheme} onLogout={handleLogout}>
      <Routes>
        <Route path="/" element={<Compatibility />} />
        <Route path="/scrape" element={<ScrapeControl />} />
        <Route path="/diff" element={<Diff />} />
        <Route path="/vehicle/:cid" element={<VehicleDetail />} />
        <Route path="/ecu/:name" element={<EcuDetail />} />
        <Route path="/heatmap" element={<Heatmap />} />
        <Route path="/suche" element={<InstantSearchPage />} />
        <Route path="/alientech" element={<AlientechCompatibility />} />
        <Route path="/alientech/scrape" element={<AlientechScrapeControl />} />
        <Route path="/magicmotorsport" element={<MagicMotorsportCompatibility />} />
        <Route path="/magicmotorsport/scrape" element={<MagicMotorsportScrapeControl />} />
        <Route path="/original-ecu-dateien" element={<DynoecuCompatibility />} />
        <Route path="/original-ecu-dateien/scrape" element={<DynoecuScrapeControl />} />
        <Route path="/wissen/bosch-ecu-referenzen" element={<BoschEcuReferences />} />
      </Routes>
    </Shell>
  );
}

function App() {
  const [theme, toggleTheme] = useTheme();
  const [authState, setAuthState] = useState("checking"); // checking | authenticated | anonymous

  useEffect(() => {
    const checkStatus = () =>
      api
        .get("/auth/status")
        .then((res) =>
          setAuthState(res.data.authenticated ? "authenticated" : "anonymous"),
        )
        .catch(() => setAuthState("anonymous"));

    checkStatus();

    const onUnauthorized = () => setAuthState("anonymous");
    window.addEventListener("auth:unauthorized", onUnauthorized);
    return () => window.removeEventListener("auth:unauthorized", onUnauthorized);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // ignore - we clear local state regardless
    }
    setAuthState("anonymous");
  }, []);

  return (
    <div className="App">
      <Toaster theme={theme} richColors position="top-right" />
      <BrowserRouter>
        <AppRoutes
          theme={theme}
          toggleTheme={toggleTheme}
          authState={authState}
          setAuthState={setAuthState}
          handleLogout={handleLogout}
        />
      </BrowserRouter>
    </div>
  );
}

export default App;
